export type ParsedItineraryHighlight = {
  dayNumber: number;
  title: string;
  raw: string;
};

export type RecommendationItineraryItemDraft = {
  title: string;
  description?: string;
  category: 'activity';
  startDate: Date;
  startTime: 'flexible';
  source: 'ai';
  status: 'planned';
  createdBy: string;
  documents: [];
};

const DAY_HIGHLIGHT_PATTERN = /^Day\s+(\d+)\s*[:\-–—]\s*(.+)$/i;

export function clampItineraryHighlightsToDuration(
  highlights: string[],
  durationDays?: number,
): string[] {
  if (!durationDays || durationDays <= 0) {
    return highlights.filter((line) => line.trim().length > 0);
  }

  return highlights.filter((rawLine) => {
    const line = rawLine.trim();
    if (!line) {
      return false;
    }
    const match = DAY_HIGHLIGHT_PATTERN.exec(line);
    if (!match) {
      return true;
    }
    const dayNumber = Number.parseInt(match[1], 10);
    return Number.isFinite(dayNumber) && dayNumber <= durationDays;
  });
}

/**
 * Parse strings like "Day 1: Arrive in London & explore the South Bank".
 */
export function parseItineraryHighlight(line: string, fallbackDayNumber: number): ParsedItineraryHighlight | null {
  const trimmed = line.trim();
  if (!trimmed) {
    return null;
  }

  const match = DAY_HIGHLIGHT_PATTERN.exec(trimmed);
  if (match) {
    const dayNumber = Number.parseInt(match[1], 10);
    const title = match[2].trim();
    if (!Number.isFinite(dayNumber) || dayNumber < 1 || !title) {
      return null;
    }
    return { dayNumber, title, raw: trimmed };
  }

  return {
    dayNumber: fallbackDayNumber,
    title: trimmed,
    raw: trimmed,
  };
}

export function buildItineraryItemsFromHighlights(args: {
  highlights: string[];
  tripStartDate: Date;
  userId: string;
  destination?: string;
  durationDays?: number;
}): RecommendationItineraryItemDraft[] {
  const { highlights, tripStartDate, userId, destination, durationDays } = args;
  const normalizedStart = new Date(tripStartDate);
  normalizedStart.setHours(12, 0, 0, 0);

  const items: RecommendationItineraryItemDraft[] = [];
  const scopedHighlights = clampItineraryHighlightsToDuration(highlights, durationDays);

  scopedHighlights.forEach((line, index) => {
    const parsed = parseItineraryHighlight(line, index + 1);
    if (!parsed) {
      return;
    }

    const dayDate = new Date(normalizedStart);
    dayDate.setDate(normalizedStart.getDate() + (parsed.dayNumber - 1));

    items.push({
      title: parsed.title,
      description: destination ? `${parsed.raw} (${destination})` : parsed.raw,
      category: 'activity',
      startDate: dayDate,
      startTime: 'flexible',
      source: 'ai',
      status: 'planned',
      createdBy: userId,
      documents: [],
    });
  });

  return items;
}
